// Info drawer functionality (opens from slider/spec info triggers)
(function() {
  // Gate noisy debug logging behind either:
  // - `window.DYC_DEBUG = true` (e.g., in theme editor preview)
  // - `?dyc_debug=1` in the URL
  const DYC_DEBUG =
    (typeof window !== 'undefined' && (window.DYC_DEBUG === true || window.DYC_DEBUG === 'true')) ||
    (typeof window !== 'undefined' && /(?:\?|&)dyc_debug=1\b/.test(window.location.search));

  const console = DYC_DEBUG
    ? window.console
    : {
        log() {},
        info() {},
        debug() {},
        warn() {},
        // Keep errors visible even when debug is off
        error:
          typeof window !== 'undefined' && window.console && window.console.error
            ? window.console.error.bind(window.console)
            : function () {},
      };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfoDrawer);
  } else {
    initInfoDrawer();
  }

  function initInfoDrawer() {
    console.log('🎯 Info Drawer: Initializing...');

    const drawer = document.querySelector('[data-info-drawer]');
    const overlay = document.querySelector('[data-info-overlay]');
    const closeBtn = drawer ? drawer.querySelector('[data-info-close]') : null;
    const titleEl = drawer ? drawer.querySelector('[data-info-title]') : null;
    const bodyEl = drawer ? drawer.querySelector('[data-info-body]') : null;

    console.log('🎯 Elements found:', {
      drawer: !!drawer,
      overlay: !!overlay,
      closeBtn: !!closeBtn,
      titleEl: !!titleEl,
      bodyEl: !!bodyEl
    });

    if (!drawer || !overlay || !titleEl || !bodyEl) {
      console.warn('❌ Info drawer or required elements not found!');
      return;
    }

    console.log('✅ All elements found! Setting up event listeners...');

    // Track touch movement to prevent drawer opening during scroll
    let touchMoved = false;
    let touchStartY = 0;
    const TOUCH_THRESHOLD = 10; // pixels of movement to consider it a scroll

    document.addEventListener('touchstart', (e) => {
      touchMoved = false;
      if (e.touches && e.touches[0]) {
        touchStartY = e.touches[0].clientY;
      }
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      if (e.touches && e.touches[0]) {
        const touchY = e.touches[0].clientY;
        if (Math.abs(touchY - touchStartY) > TOUCH_THRESHOLD) {
          touchMoved = true;
        }
      }
    }, { passive: true });

    // Track current snap point (moved to outer scope for open/close access)
    let currentSnapPoint = 0;
    
    // Track scroll position for body lock (iOS Safari fix)
    let savedScrollPosition = 0;

    // Lock body scroll (iOS Safari compatible)
    const lockBodyScroll = () => {
      savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.top = `-${savedScrollPosition}px`;
      document.body.classList.add('info-drawer-open', 'drawer-open');
    };

    // Unlock body scroll and restore position
    const unlockBodyScroll = () => {
      document.body.classList.remove('info-drawer-open', 'drawer-open');
      document.body.style.top = '';
      window.scrollTo(0, savedScrollPosition);
    };
    
    const open = ({ title, html, drawerType }) => {
      console.log('🚀 Opening info drawer:', { title, drawerType });

      // Reset snap point to full open
      currentSnapPoint = 0;
      drawer.classList.remove('is-half');
      drawer.classList.add('is-full');
      drawer.style.removeProperty('transform');

      // IMPORTANT: Set content BEFORE starting animation to prevent Android jitter
      titleEl.textContent = title || 'Information';
      bodyEl.innerHTML = html && html.trim().length ? html : '<p>No information available for this item yet.</p>';

      // Apply drawer type class
      drawer.classList.remove('drawer-type-modal', 'drawer-type-sidebar');
      drawer.classList.add(`drawer-type-${drawerType || 'modal'}`);

      // The markup ships aria-hidden="true". style.css has a global rule
      // `[aria-hidden="true"] button { visibility: hidden; pointer-events: none }`,
      // so leaving it stale makes the close (X) button invisible AND unclickable
      // while the drawer is open — and hides the drawer from screen readers.
      drawer.setAttribute('aria-hidden', 'false');

      // Small delay to let DOM settle before animation (prevents Android jitter and blur hang)
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        overlay.classList.add('is-open');
        // iOS Safari: Lock body scroll with position fixed + scroll position save
        lockBodyScroll();
        console.log('✅ Info drawer opened at full height');
      });
    };

    const close = () => {
      console.log('🔒 Closing info drawer...');
      drawer.classList.remove('is-open', 'is-half', 'is-full', 'is-dragging');
      overlay.classList.remove('is-open');
      // Reset snap point for next open
      currentSnapPoint = 0;
      // Clear all inline styles that might have been set during drag/snap
      drawer.style.removeProperty('transform');
      drawer.style.transition = '';
      overlay.style.opacity = '';
      overlay.style.transition = '';
      // Restore hidden-from-assistive-tech state (see note in open()).
      drawer.setAttribute('aria-hidden', 'true');
      // iOS Safari: Unlock body scroll and restore position
      unlockBodyScroll();
      console.log('✅ Info drawer closed');
    };

    // Delegate clicks from any info trigger (sliders + specs + product tips)
    document.addEventListener('click', (e) => {
      // Skip if user was scrolling (touch moved significantly)
      if (touchMoved) {
        touchMoved = false;
        return;
      }

      const trigger = e.target.closest('[data-finish-slider-info-trigger], [data-finish-spec-info-trigger], [data-product-tips-trigger]');
      if (!trigger) return;

      e.preventDefault();
      e.stopPropagation();

      const title = trigger.getAttribute('data-info-title') || 'Information';
      const drawerType = trigger.getAttribute('data-drawer-type') || 'modal';

      // Check for template with content
      const tpl = trigger.querySelector('template[data-info-content]');
      const html = tpl ? tpl.innerHTML : '';

      open({ title, html, drawerType });
    }, true);

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', close);
    }

    // Click overlay to close
    overlay.addEventListener('click', close);

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });

    // ========================================
    // SNAP POINT DRAWER (ChatGPT-style)
    // Supports: Full (0%), Half (50%), Closed (100%)
    // Content remains scrollable at any snap point
    // ========================================
    let swipeStartY = 0;
    let swipeCurrentY = 0;
    let swipeStartTime = 0;
    let isSwiping = false;
    // currentSnapPoint is declared above in the open function scope
    
    // Snap points as percentage of drawer height from top
    // 0 = fully open, 0.5 = half height, 1 = closed
    const SNAP_POINTS = [0, 0.5, 1];
    const VELOCITY_THRESHOLD = 0.5; // pixels per millisecond for fast flick
    
    // Get the translateY value for a snap point
    const getSnapTranslateY = (snapPoint, drawerHeight) => {
      // snapPoint 0 = translateY(0), 0.5 = translateY(50%), 1 = translateY(100%)
      return snapPoint * drawerHeight;
    };
    
    // Find nearest snap point based on current position and velocity
    const findNearestSnapPoint = (currentPercent, velocity) => {
      // If fast swipe down, go to next snap point
      if (velocity > VELOCITY_THRESHOLD) {
        const nextPoints = SNAP_POINTS.filter(p => p > currentSnapPoint);
        return nextPoints.length > 0 ? nextPoints[0] : 1;
      }
      // If fast swipe up, go to previous snap point
      if (velocity < -VELOCITY_THRESHOLD) {
        const prevPoints = SNAP_POINTS.filter(p => p < currentSnapPoint).reverse();
        return prevPoints.length > 0 ? prevPoints[0] : 0;
      }
      // Otherwise snap to nearest
      let nearest = SNAP_POINTS[0];
      let minDist = Math.abs(currentPercent - SNAP_POINTS[0]);
      for (const point of SNAP_POINTS) {
        const dist = Math.abs(currentPercent - point);
        if (dist < minDist) {
          minDist = dist;
          nearest = point;
        }
      }
      return nearest;
    };
    
    // Animate to a snap point
    const animateToSnapPoint = (snapPoint, drawerHeight) => {
      drawer.classList.remove('is-dragging');
      drawer.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      overlay.style.transition = 'opacity 0.3s ease';
      
      const translateY = getSnapTranslateY(snapPoint, drawerHeight);
      drawer.style.setProperty('transform', `translateY(${translateY}px)`, 'important');
      
      // Adjust overlay opacity based on snap point
      const overlayOpacity = 1 - (snapPoint * 0.6);
      overlay.style.opacity = String(Math.max(0, overlayOpacity));
      
      currentSnapPoint = snapPoint;
      
      // Update drawer state class
      drawer.classList.remove('is-half', 'is-full');
      if (snapPoint === 0) {
        drawer.classList.add('is-full');
      } else if (snapPoint === 0.5) {
        drawer.classList.add('is-half');
      }
      
      setTimeout(() => {
        drawer.style.transition = '';
        // If closed, actually close the drawer
        if (snapPoint === 1) {
          drawer.style.removeProperty('transform');
          overlay.style.transition = '';
          overlay.style.opacity = '';
          close();
          currentSnapPoint = 0; // Reset for next open
        }
      }, 300);
    };

    drawer.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      const drawerRect = drawer.getBoundingClientRect();
      const touchOffsetY = touch.clientY - drawerRect.top;

      // Allow swipe from top 100px of drawer (header/handle area)
      if (touchOffsetY <= 100) {
        swipeStartY = touch.clientY;
        swipeCurrentY = touch.clientY;
        swipeStartTime = Date.now();
        isSwiping = true;
        // Add dragging class to override CSS transform
        drawer.classList.add('is-dragging');
        console.log('🖐️ Swipe started at Y:', touchOffsetY, 'currentSnapPoint:', currentSnapPoint);
      }
    }, { passive: true });

    drawer.addEventListener('touchmove', (e) => {
      if (!isSwiping) return;

      const touch = e.touches[0];
      swipeCurrentY = touch.clientY;
      const deltaY = swipeCurrentY - swipeStartY;
      const drawerHeight = drawer.offsetHeight;
      
      // Calculate current position including the snap point offset
      const currentOffset = getSnapTranslateY(currentSnapPoint, drawerHeight);
      const newTranslateY = currentOffset + deltaY;
      
      // Clamp to valid range (0 to drawerHeight)
      const clampedTranslateY = Math.max(0, Math.min(newTranslateY, drawerHeight));
      
      // iOS FIX: Use setProperty with 'important' priority
      drawer.style.setProperty('transform', `translateY(${clampedTranslateY}px)`, 'important');
      
      // Fade overlay based on position
      const progress = clampedTranslateY / drawerHeight;
      overlay.style.opacity = String(Math.max(0, 1 - (progress * 0.6)));
    }, { passive: true });

    drawer.addEventListener('touchend', () => {
      if (!isSwiping) return;

      const deltaY = swipeCurrentY - swipeStartY;
      const elapsed = Date.now() - swipeStartTime;
      const velocity = elapsed > 0 ? deltaY / elapsed : 0;
      const drawerHeight = drawer.offsetHeight;
      
      // Calculate where we ended up as a percentage
      const currentOffset = getSnapTranslateY(currentSnapPoint, drawerHeight);
      const finalTranslateY = Math.max(0, Math.min(currentOffset + deltaY, drawerHeight));
      const finalPercent = finalTranslateY / drawerHeight;

      console.log('🖐️ Swipe ended:', { 
        deltaY, 
        velocity: velocity.toFixed(2), 
        finalPercent: (finalPercent * 100).toFixed(0) + '%',
        currentSnapPoint 
      });

      // Find the snap point to animate to
      const targetSnapPoint = findNearestSnapPoint(finalPercent, velocity);
      console.log('📍 Snapping to:', targetSnapPoint === 0 ? 'full' : targetSnapPoint === 0.5 ? 'half' : 'closed');
      
      animateToSnapPoint(targetSnapPoint, drawerHeight);

      isSwiping = false;
      swipeStartY = 0;
      swipeCurrentY = 0;
    }, { passive: true });

    drawer.addEventListener('touchcancel', () => {
      drawer.classList.remove('is-dragging');
      drawer.style.transition = '';
      // Snap back to current snap point
      const drawerHeight = drawer.offsetHeight;
      animateToSnapPoint(currentSnapPoint, drawerHeight);
      isSwiping = false;
    }, { passive: true });

    // ========================================
    // SCROLL CONTAINMENT - prevent background scroll on Android/iOS
    // Content area should scroll freely, only block at boundaries
    // ========================================
    const drawerContent = drawer.querySelector('.info-drawer__content');
    let contentTouchStartY = 0;
    let contentScrollStartTop = 0;

    if (drawerContent) {
      drawerContent.addEventListener('touchstart', (e) => {
        contentTouchStartY = e.touches[0].clientY;
        contentScrollStartTop = drawerContent.scrollTop;
        // DEBUG: Log scroll state on touch start (use window.console to bypass debug flag)
        const { scrollTop, scrollHeight, clientHeight } = drawerContent;
        window.console.log('📜 Content touch start:', { 
          scrollTop, scrollHeight, clientHeight, 
          hasScrollableContent: scrollHeight > clientHeight,
          currentSnapPoint 
        });
      }, { passive: true });

      drawerContent.addEventListener('touchmove', (e) => {
        // Allow scrolling within content - only prevent overscroll
        const { scrollTop, scrollHeight, clientHeight } = drawerContent;
        const touchY = e.touches[0].clientY;
        const deltaY = contentTouchStartY - touchY; // positive = scrolling down

        const atTop = scrollTop <= 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
        const hasScrollableContent = scrollHeight > clientHeight;

        // DEBUG: Log on first move (use window.console to bypass debug flag)
        if (Math.abs(deltaY) < 5) {
          window.console.log('📜 Content touch move:', { 
            deltaY, scrollTop, scrollHeight, clientHeight,
            atTop, atBottom, hasScrollableContent,
            willPrevent: !hasScrollableContent || ((atTop && deltaY < 0) || (atBottom && deltaY > 0))
          });
        }

        // Only prevent default at boundaries to stop body scroll
        // But allow the content to scroll if there's scrollable content
        if (!hasScrollableContent) {
          // No scrollable content, prevent to avoid body scroll
          e.preventDefault();
        } else if ((atTop && deltaY < 0) || (atBottom && deltaY > 0)) {
          // At boundary trying to scroll past it
          e.preventDefault();
        }
        // Otherwise, let the scroll happen naturally
      }, { passive: false });
    }

    // Prevent overlay touch from scrolling body
    overlay.addEventListener('touchmove', (e) => {
      e.preventDefault();
    }, { passive: false });

    // Prevent touch on drawer header from scrolling body (except when swiping to close)
    const drawerHeader = drawer.querySelector('.info-drawer__header');
    if (drawerHeader) {
      drawerHeader.addEventListener('touchmove', (e) => {
        if (!isSwiping) {
          e.preventDefault();
        }
      }, { passive: false });
    }

    console.log('✅ Info Drawer: Fully initialized and ready');
  }
})();

